import type { AddVersionChoice } from 'components/documentation/AddVersionDialog';
import type { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import type { ExportedVersionOptions } from 'shared/domain/issue/linkedDocumentation/operations';
import { removeFileExtension } from 'shared/utils/fileExtensions';

/** What the export reads of a file of the issue to name it: a gallery file has both. */
interface ExportedFile {
  title?: string;
  data?: { name?: string; extension?: string };
}

export function getExportedFileName(file: ExportedFile): string {
  return file.data?.name || file.title || '';
}

/** The name the backend gives an exported version (D10): the file's name without its extension. */
export function getDefaultVersionName(file: ExportedFile): string {
  return removeFileExtension(getExportedFileName(file));
}

/** Withdrawn versions keep their numbers, so the next one follows the highest of them. */
export function getNextVersionNumber(documentation: Pick<DocumentationModel, 'versions'>): number {
  return documentation.versions.reduce((highest, version) => Math.max(highest, version.number ?? 0), 0) + 1;
}

/** What the user chose in the dialog that adds the file as a version. */
export type ExportedVersionChoice = AddVersionChoice;

/** The name goes to the backend only when the user changed it: otherwise the backend's own is kept. */
export function toExportedVersionOptions(
  { name, makeCurrent }: ExportedVersionChoice,
  defaultName: string
): ExportedVersionOptions {
  const trimmedName = name.trim();
  return {
    makeCurrent,
    ...(trimmedName && trimmedName !== defaultName ? { name: trimmedName } : {}),
  };
}
